import React, { ChangeEvent, useEffect, useState, FC, useRef } from "react";
import { useController, UseFormSetValue, useWatch } from "react-hook-form";
import usePatchObservationQuestion from "../../hooks/usePatchObservationQuestion/usePatchObservationQuestion";
import { toast } from "react-toastify";
import CustomToast from "src/components/CustomToast/CustomToast";
import { AzureImage } from "src/components/AssetUploaders/AzureImage";
import { Upload, Info } from "lucide-react";

interface Props {
	questionIndex: number;
	control: any;
	error: boolean;
	upload: (file: File, uploadType: string) => void;
	uuid: string | null;
	done: boolean;
	uploading: boolean;
	setValue: UseFormSetValue<AdminObservationForm>;
	setUUID: React.Dispatch<React.SetStateAction<string | null>>;
	questionId: number | null;
}
const HeadingCoverUpload: FC<Props> = ({
	questionIndex,
	control,
	upload,
	uuid,
	uploading,
	setValue,
	setUUID,
	error,
	questionId
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [isDragging, setIsDragging] = useState(false);

	const givenAnswer = useWatch({
		name: `questions.${questionIndex}.image`,
		control
	});

	const { mutate: patchObservationImage } = usePatchObservationQuestion();

	const imageUUID = useWatch({
		name: `questions.${questionIndex}.image`,
		control
	});

	const {
		field: { onChange: uuidOnChange, ...inputProps }
	} = useController({
		name: `questions.${questionIndex}.image`,
		control
	});

	const processFile = (file: File) => {
		const reader = new FileReader();

		reader.onload = (progressEvent: ProgressEvent<FileReader>) => {
			if (!progressEvent?.target) return;

			setValue(
				`questions.${questionIndex}.imagePreview`,
				progressEvent.target.result as string
			);

			upload(file, "images");
		};

		reader.readAsDataURL(file);
	};

	const onFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
		const target = event.target as HTMLInputElement;
		if (!target?.files?.[0]) return;

		const file = target.files[0];
		if (!validateFile(file)) {
			event.target.value = "";
			return;
		}

		processFile(file);
		event.target.value = "";
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
	};

	const validateFile = (file: File): boolean => {
		const validTypes = ["image/jpeg", "image/png"];
		const maxSize = 3 * 1024 * 1024;
		if (!validTypes.includes(file.type)) {
			toast.error(
				<CustomToast
					type="Error"
					message="Only JPG and PNG files are allowed"
				/>
			);
			return false;
		}
		if (file.size > maxSize) {
			toast.error(
				<CustomToast type="Error" message="Image must be under 3 MB" />
			);
			return false;
		}
		return true;
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		const file = e.dataTransfer.files[0];
		if (file && validateFile(file)) processFile(file);
	};

	useEffect(() => {
		if (!uuid) return;

		setValue(`questions.${questionIndex}.image`, uuid);
		uuidOnChange(uuid);
	}, [uuid]);

	const onSuccess = () => {
		setValue(`questions.${questionIndex}.imagePreview`, "");
		setValue(`questions.${questionIndex}.image`, "");
		setUUID("");
		uuidOnChange("");
	};

	const onError = () => {
		toast.error(
			<CustomToast type="Error" message="Please try deleting the image again" />
		);
	};

	const deleteMedia = () => {
		if (!questionId) return;

		patchObservationImage(
			{
				id: questionId,
				image_cover_id: null
			},
			{ onSuccess, onError }
		);
	};

	const isImageUploaded = uuid || givenAnswer;

	return (
		<div className="pt-6 mt-2 border-t border-border">
			<label className="text-sm font-medium text-zinc-600 mb-1 block">
				Cover image (optional)
			</label>
			<p className="text-[13px] text-muted-foreground mb-4">
				JPG or PNG, 1000 x 650px, under 3 MB
			</p>

			<input
				type="file"
				onChange={onFileSelected}
				accept="image/jpeg,image/png"
				{...inputProps}
				value=""
				name={`questions.${questionIndex}.image`}
				ref={inputRef}
				className="hidden"
				aria-label="file-upload"
			/>

			<div className="max-w-[480px]">
				{isImageUploaded ? (
					<>
						<div className="mb-3 rounded-lg overflow-hidden border border-border bg-subtle max-h-[300px]">
							<AzureImage
								uuid={imageUUID}
								imageProfile="retail-app-observation-heading"
								linkToFull={true}
								style={{
									objectFit: "contain",
									width: "100%",
									height: "100%",
									maxHeight: "300px"
								}}
							/>
						</div>
						<div className="flex items-center gap-2">
							<button
								type="button"
								className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-foreground bg-white text-foreground text-sm font-medium cursor-pointer hover:bg-foreground hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								onClick={() => inputRef.current?.click()}
								disabled={uploading}
							>
								{uploading ? "Uploading..." : "Change image"}
							</button>
							<button
								type="button"
								className="inline-flex items-center justify-center px-5 h-9 rounded-lg border border-red-300 bg-white text-red-600 text-sm font-medium cursor-pointer hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors"
								onClick={deleteMedia}
							>
								Remove
							</button>
						</div>
					</>
				) : (
					<div
						role="button"
						tabIndex={0}
						onClick={() => inputRef.current?.click()}
						onKeyDown={(e) => {
							if (e.key === "Enter" || e.key === " ") {
								e.preventDefault();
								inputRef.current?.click();
							}
						}}
						onDragOver={handleDragOver}
						onDragLeave={handleDragLeave}
						onDrop={handleDrop}
						className={[
							"flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer",
							isDragging
								? "border-primary bg-primary-lighter"
								: "border-border bg-white hover:border-primary-light hover:bg-primary-lighter/50"
						].join(" ")}
					>
						{uploading ? (
							<div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
						) : (
							<Upload className="h-6 w-6 text-muted-foreground" />
						)}
						<div>
							<p className="text-sm font-medium text-foreground">
								{uploading
									? "Uploading..."
									: "Drag image here or click to browse"}
							</p>
							<p className="text-xs text-muted-foreground mt-1">
								JPG or PNG, under 3 MB
							</p>
						</div>
					</div>
				)}
			</div>

			{error && (
				<div className="flex items-center gap-1 text-destructive mt-2">
					<Info size={16} />
					<span className="text-sm">
						There was an issue uploading your file, please try again.
					</span>
				</div>
			)}
		</div>
	);
};

export default HeadingCoverUpload;
